import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import useConnectionStore from './connectionStore';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Store Apollo client reference
let apolloClient = null;

export const setApolloClient = (client) => {
  apolloClient = client;
};

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      error: null,

      // Computed value
      isAuthenticated: () => !!get().token,

      // Sign up action
      signup: async (username, email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API_URL}/api/auth/signup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, email, password })
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || data.errors?.[0]?.msg || 'Signup failed');
          }

          set({ 
            user: data.user, 
            token: data.accessToken,
            isLoading: false,
            error: null 
          });

          // Fetch candidate states after signup
          useConnectionStore.getState().fetchCandidateStates(data.accessToken);

          return { success: true };
        } catch (error) {
          set({ isLoading: false, error: error.message });
          return { success: false, error: error.message };
        }
      },

      // Sign in action
      signin: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API_URL}/api/auth/signin`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || data.errors?.[0]?.msg || 'Sign in failed');
          }

          set({ 
            user: data.user, 
            token: data.accessToken,
            isLoading: false,
            error: null 
          });

          // Fetch candidate states after signin
          useConnectionStore.getState().fetchCandidateStates(data.accessToken);

          return { success: true };
        } catch (error) {
          set({ isLoading: false, error: error.message });
          return { success: false, error: error.message };
        }
      },

      // Sign out action
      signout: async () => {
        const currentToken = get().token;
        
        // Call logout endpoint to delete session from database
        if (currentToken) {
          try {
            await fetch(`${API_URL}/api/auth/logout`, {
              method: 'POST',
              headers: { 
                'Authorization': `Bearer ${currentToken}`
              }
            });
          } catch (error) {
            console.error('Logout error:', error);
          }
        }

        useConnectionStore.getState().clearStates();
        set({ user: null, token: null, error: null });
        
        // Clear Apollo cache
        if (apolloClient) {
          apolloClient.clearStore().catch(err => {
            console.error('Error clearing Apollo cache:', err);
          });
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },

      // Update token (called automatically when server sends new token)
      updateToken: (token) => {
        set({ token });
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user, 
        token: state.token
      })
    }
  )
);

export default useAuthStore;
